import React, {useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {deleteNote} from '../api';
import type {Attachment} from '../types/attachment';
import {BACKEND_URL} from '../config';
import '../components/Editor.css';
import {Editor} from '@tiptap/react';
import ConfirmModal from './ConfirmModal';
import {useNote} from '../hooks/useNote';
import {useMutation, useQueryClient} from '@tanstack/react-query';

import {ROOT_LEVEL} from "../utils/constants";
import {NoteEditor} from "./NoteEditor";

const NoteView: React.FC = () => {
    const {id} = useParams();
    const navigate = useNavigate();
    const [showConfirm, setShowConfirm] = useState(false);
    const [editor, setEditor] = useState<Editor>();
    const queryClient = useQueryClient();

    const {
        note,
        isLoading,
        error
    } = useNote(id ? Number(id) : null);

    const deleteMutation = useMutation({
        mutationFn: deleteNote,
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['notes']});
            navigate('/');
        }
    });

    const handleEdit = () => {
        navigate(`/notes/${id}/edit`, {
            state: {
                note: note ? note : undefined,
            },
        });
    };

    const handleDelete = () => {
        setShowConfirm(true);
    };

    const handleConfirm = () => {
        setShowConfirm(false);
        if (id) {
            deleteMutation.mutate(Number(id));
        }
    };

    const handleCancelModal = () => {
        setShowConfirm(false);
    };

    // Update editor content when note is loaded
    useEffect(() => {
        if (note?.content) {
            editor?.commands.setContent(note.content);
        }
    }, [note, editor]);

    if (isLoading) return <div className="text-center p-8">Loading...</div>;
    if (error) return <div
        className="text-red-500 p-8">{error instanceof Error ? error.message : 'An error occurred'}</div>;
    if (!note) return <div className="text-center p-8">Note not found.</div>;

    return (
        <div className="max-w-6xl mx-auto mt-8 px-4 sm:px-6 lg:px-8">
            {/* Top Navigation Bar */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                {/* Left side: Back button and Category path */}
                <div className="flex items-center gap-3 text-sm">
                    <button
                        onClick={() => navigate(`/notes${note.category_id ? `?category=${note.category_id}` : `?category=${ROOT_LEVEL.id}`}`)}
                        className="inline-flex items-center gap-2 text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 transition-colors"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20"
                             fill="currentColor">
                            <path fillRule="evenodd"
                                  d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
                                  clipRule="evenodd"/>
                        </svg>
                        <span>Back to Notes</span>
                    </button>
                    <span className="text-gray-400 dark:text-gray-500">/</span>
                    <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24"
                             stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                  d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"/>
                        </svg>
                        {note.category_breadcrumb && note.category_breadcrumb.length > 0 ? note.category_breadcrumb?.join(' > ') : 'Uncategorized'}
                    </div>
                </div>

                {/* Right side: Action buttons */}
                <div className="flex items-center gap-3 self-end sm:self-auto">
                    <button
                        onClick={handleEdit}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-teal-500 hover:bg-teal-600 text-white transition-colors"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24"
                             stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                        </svg>
                        Edit
                    </button>
                    <button
                        onClick={handleDelete}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-red-500 hover:bg-red-600 text-white transition-colors"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24"
                             stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                        </svg>
                        Delete
                    </button>
                </div>
            </div>

            <div
                className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-lg p-6 sm:p-8 lg:p-10">
                {/* Note Content */}
                <NoteEditor
                    note={note}
                    editMode={false}
                    onCategoryChange={() => {
                    }}
                    onEditorReady={(editor) => {
                        setEditor(editor)
                    }}
                    selectedCategoryId={note.category_id}
                    isSaving={true}
                    onSave={() => {
                    }}
                    showSaveButton={false}
                />
                {/*<EditorContent editor={editor}/>*/}
                <hr className="my-8 border-t border-gray-200 dark:border-gray-600"/>

                <div className="mt-8 pt-6 border-t border-gray-100 dark:border-gray-700">
                    <h3 className="text-base font-semibold mb-3 text-gray-900 dark:text-gray-100">Attachments</h3>
                    {(!note.attachments || note.attachments.length === 0) ? (
                        <p className="text-gray-500 text-sm">No attachments</p>
                    ) : (
                        <div className="grid grid-cols-1 gap-1">
                            {note.attachments.map((attachment: Attachment) => (
                                <div key={attachment.id} className="flex items-center gap-2 py-1">
                                    {attachment.mime_type.startsWith('image/') ? (
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-400"
                                             fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <rect x="3" y="3" width="18" height="18" rx="2" fill="#e0f2fe"/>
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                  d="M8 17l4-4 4 4M8 13l4-4 4 4"/>
                                        </svg>
                                    ) : (
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400"
                                             fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                  d="M7 21h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                                        </svg>
                                    )}
                                    <a
                                        href={`${BACKEND_URL}/${attachment.file_path}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-xs font-medium text-gray-700 truncate hover:underline"
                                    >
                                        {attachment.filename}
                                    </a>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div
                    className="note-meta mt-8 pt-6 border-t border-gray-100 dark:border-gray-700 flex flex-col sm:flex-row justify-end gap-4 text-sm text-gray-500 dark:text-gray-400">
          <span className="note-date flex items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24"
                 stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
            </svg>
            Created: {new Date(note.created_at).toLocaleString()}
          </span>
                    <span className="note-date flex items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24"
                 stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
            Last updated: {new Date(note.updated_at).toLocaleString()}
          </span>
                </div>
            </div>

            <ConfirmModal
                open={showConfirm}
                message="Are you sure you want to delete this note? This action cannot be undone."
                onConfirm={handleConfirm}
                onCancel={handleCancelModal}
                confirmText="Delete"
                cancelText="Cancel"
            />
        </div>
    );
};

export default NoteView; 